import { useState, useEffect } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { toast } from "react-toastify";
import { PageContainer, DataTable, Field, StatusBadge } from "components/ui";
import ExportButtons from "components/ui/ExportButtons";
const typeTone = {
  ASSET: "brand",
  LIABILITY: "warning",
  EQUITY: "success",
  REVENUE: "info",
  EXPENSE: "danger",
};

const TrialBalance = () => {
  const axiosPrivate = useAxiosPrivate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axiosPrivate.get(`/api/accountant/trial-balance?asOf=${asOfDate}`);
      setRows(res.data);
    } catch (_) {
      toast.error("Failed to load trial balance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [asOfDate]);

  const totals = rows.reduce(
    (acc, r) => ({ debit: acc.debit + Number(r.debitSum || 0), credit: acc.credit + Number(r.creditSum || 0) }),
    { debit: 0, credit: 0 }
  );
  const isBalanced = Math.abs(totals.debit - totals.credit) < 0.005;

  return (
    <PageContainer>
      <DataTable
        title="Trial balance"
        subtitle="Account balances as of selected date"
        toolbar={
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 200 }}>
              <Field
                label=""
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
              />
            </div>

            <ExportButtons
              path="trial-balance"
              params={{ asOf: asOfDate }}
              filename={`trial-balance-${asOfDate}`}
            />
          </div>
        }
        loading={loading}
        data={rows}
        rowKey={(r) => r.accountCode}
        search={true}
        searchPlaceholder="Search accounts…"
        pageSize={20}
        emptyTitle="No data available"
        columns={[
          {
            key: "accountName", header: "Account",
            render: (r) => (
              <div>
                <div style={{ fontWeight: 600 }}>{r.accountName}</div>
                <div className="t-mono t-muted" style={{ fontSize: 12 }}>{r.accountCode}</div>
              </div>
            ),
          },
          { key: "accountType", header: "Type", render: (r) => <StatusBadge tone={typeTone[r.accountType] || "neutral"}>{r.accountType}</StatusBadge> },
          { key: "debitSum", header: "Debit", align: "right", render: (r) => <span className="t-mono">{Number(r.debitSum).toFixed(2)} TND</span> },
          { key: "creditSum", header: "Credit", align: "right", render: (r) => <span className="t-mono">{Number(r.creditSum).toFixed(2)} TND</span> },
        ]}
        footer={
          <div
            className="dt-foot"
            style={{
              borderTop: `2px solid ${isBalanced ? "var(--border-default)" : "var(--danger)"}`,
              background: isBalanced ? "var(--surface-muted)" : "var(--danger-bg)",
            }}
          >
            <span style={{ fontWeight: 600, color: isBalanced ? "var(--text-body)" : "var(--danger)" }}>
              {isBalanced ? "Totals (balanced)" : "⚠️ Totals do not match"}
            </span>
            <span style={{ display: "inline-flex", gap: 24 }}>
              <span className="t-mono" style={{ fontWeight: 600 }}>Debit: {totals.debit.toFixed(2)} TND</span>
              <span className="t-mono" style={{ fontWeight: 600 }}>Credit: {totals.credit.toFixed(2)} TND</span>
            </span>
          </div>
        }
      />
    </PageContainer>
  );
};

export default TrialBalance;